import { observer } from 'mobx-react';
import React, { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStoreContext } from '@stores/rootStore';
import { displayWeight, isPresent } from '@farmai-shared/LFUtils';
import { thousandSeparator } from '@services/utils';
import { LFEChart } from '@app/_components/lf-graph/LFEChart';
import { MockTraceabilityPie } from './mocks/MockPie';
import { PieSeriesOption } from 'echarts';
import Big from 'big.js';
import { StatResponse } from '@services/responseTypes';
export const TraceabilityPie: FC<{
  destinations?: StatResponse['destinations'];
  legendColors: {
    [k: string]: string;
  };
  mock?: boolean;
}> = observer(({
  destinations,
  legendColors,
  mock = false
}) => {
  const {
    userStore
  } = useContext(RootStoreContext);
  const {
    t
  } = useTranslation();
  const weightUnit = userStore.user.settings.weight_unit;
  if (!isPresent(destinations)) {
    return <MockTraceabilityPie title={t('noTraceabilityPieData')} />;
  }
  const formattedData = Object.values(destinations.reduce((acc: {
    [k: string]: {
      value: number;
      name: string;
      itemStyle: {
        [k: string]: string;
      };
    };
  }, {
    amount,
    place
  }) => {
    if (!acc[place.id]) {
      acc[place.id] = {
        value: parseFloat(amount) * userStore.user.weightUnitFactor,
        name: place.name,
        itemStyle: {
          color: legendColors[place.name]
        }
      };
    } else {
      acc[place.id].value = Big(acc[place.id].value).plus(amount).times(userStore.user.weightUnitFactor).toNumber();
    }
    return acc;
  }, {}));
  const toolTipFormatter = params => `${params.name}:  ${params.percent}% (${thousandSeparator(displayWeight(params.value, {
    minDec: 3
  }))} ${t(weightUnit)})`;
  const labelFormatter = params => `{pb| ${params.percent}%}\n {vi|${thousandSeparator(displayWeight(params.value, {
    minDec: 3
  }))} ${t(weightUnit)}}`;
  return <div className={`
      h-88
      lg:h-144
    `}>
      <LFEChart series={[{
      type: 'pie',
      radius: '60%',
      silent: mock,
      data: formattedData,
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      },
      label: {
        formatter: labelFormatter,
        rich: {
          pb: {
            color: '#1C1F28',
            fontSize: '1.4rem',
            fontWeight: 500,
            fontFamily: 'Kanit',
            padding: [0, 4]
          },
          vi: {
            fontSize: '1.25rem',
            fontWeight: 300,
            fontFamily: 'Kanit',
            color: '#BFC7E1'
          }
        }
      }
    }] as PieSeriesOption} tooltip={{
      show: !mock,
      trigger: 'item',
      formatter: toolTipFormatter,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      className: 'traceability-tooltip',
      textStyle: {
        color: 'rgb(255,255,255)',
        fontFamily: 'Kanit'
      }
    }} />
    </div>;
});